import {
	Box,
	Button,
	CardActions,
	CardContent,
	Divider,
	FormHelperText,
	Grid,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useController,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { useNavigate, useParams } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import AppRichTextEditor from '@/common/components/editor/AppRichTextEditor';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { ExerciseColor } from '@/api/enum/common.enum';
import VisuallyHiddenInput from '@/common/components/UploadInput';
import { useFilesUploader, useFileUploader } from '@/hooks/useFileUploader';
import { FileInfo } from '@/api/DTO/common.interface';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import SingleCheckbox from '@/common/components/form-inputs/SingleCheckbox';
import ImageCropper from '@/common/components/imageCropper/imageCropper';
import { AdvanceSeriesDetailResDTO } from '@/api/DTO/exercise/major.interface';
import {
	useCreateMajorDetailMutation,
	useUpdateMajorDetailMutation,
} from '@/api/exercise/majorApiSlice';

interface Props {
	type: FormType;
	details?: AdvanceSeriesDetailResDTO;
	setType: Dispatch<SetStateAction<FormType>>;
}

export default function MajorInfoForm({ type, setType, details }: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const [currentTab, setCurrentTab] = useState('zh');
	const [createMajorDetail] = useCreateMajorDetailMutation();
	const [updateMajorDetail] = useUpdateMajorDetailMutation();
	const { uploadFile } = useFileUploader();
	const { uploadFiles } = useFilesUploader();
	const [coverFile, setCoverFile] = useState<FileInfo | null>(null);
	const imageInputRef = useRef<HTMLInputElement>(null);
	const [showCropper, setShowCropper] = useState(false);
	const [thumbnailBase, setThumbnailBase] = useState<{
		base64: any;
		name: string;
	} | null>(null);
	const { majorId } = useParams();

	const colorList = [
		{ id: ExerciseColor.AQUA, name: t('day21.AQUA') },
		{ id: ExerciseColor.ORANGE, name: t('day21.ORANGE') },
		{ id: ExerciseColor.CORAL, name: t('day21.CORAL') },
		{ id: ExerciseColor.PINK, name: t('day21.PINK') },
		{ id: ExerciseColor.PURPLE, name: t('day21.PURPLE') },
		{ id: ExerciseColor.LAVENDER, name: t('day21.LAVENDER') },
		{ id: ExerciseColor.BLUE, name: t('day21.BLUE') },
		// { id: ExerciseColor.CX, name: t('meditation.CX') },
		// { id: ExerciseColor.MTR, name: t('meditation.MTR') },
	];

	const defaultValues: DefaultValues<AdvanceSeriesDetailResDTO> = {
		contentZh: details?.contentZh || '',
		contentEn: details?.contentEn || '',
	};
	useEffect(() => {
		if (details?.titleEn) methods.setValue('titleEn', details.titleEn);
		if (details?.titleZh) methods.setValue('titleZh', details.titleZh);
		if (details?.authorZh) methods.setValue('authorZh', details.authorZh);
		if (details?.authorEn) methods.setValue('authorEn', details.authorEn);
		if (details?.authorDescZh)
			methods.setValue('authorDescZh', details.authorDescZh);
		if (details?.authorDescEn)
			methods.setValue('authorDescEn', details.authorDescEn);
		if (Number(details?.color) > -1) methods.setValue('color', details?.color);
		if (details?.password) methods.setValue('password', details.password);
		if (details?.coverImage) {
			setCoverFile(details.coverImage);
			methods.setValue('cover', 'file');
		}
		if (details?.hasContent) methods.setValue('hasContent', details.hasContent);
		if (details?.contentEn) methods.setValue('contentEn', details.contentEn);
		if (details?.contentZh) methods.setValue('contentZh', details.contentZh);
	}, [details]);

	const formSchema = Yup.object().shape({
		titleZh: Yup.string().required(t('errorMessage.required')),
		authorZh: Yup.string().required(t('errorMessage.required')),
		authorDescZh: Yup.string().required(t('errorMessage.required')),
		cover: Yup.string().required(t('errorMessage.pleaseSelect')),
		color: Yup.string().required(t('errorMessage.pleaseSelect')),
		password: Yup.string().required(t('errorMessage.pleaseSelect')),
	});
	const methods = useForm<AdvanceSeriesDetailResDTO>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const coverField = useController({
		name: 'cover',
		control: methods.control,
	});

	const watchHasContent = methods.watch('hasContent');

	useEffect(() => {
		const subscription = methods.watch((value) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<AdvanceSeriesDetailResDTO> = async (data) => {
		console.log(data);
		const obj: AdvanceSeriesDetailResDTO = { ...data };
		if (!methods.getValues('hasContent')) obj.hasContent = false;
		if (coverFile) {
			obj.coverImage = coverFile;
		}
		if (methods.getValues('hasContent')) {
			obj.contentEn = methods.getValues('contentEn');
			obj.contentZh = methods.getValues('contentZh');
		} else {
			obj.contentEn = '';
			obj.contentZh = '';
			delete obj.mediaType;
		}
		const response =
			type == FormType.create
				? await createMajorDetail(obj)
				: await updateMajorDetail({ ...obj, id: majorId || '' });
		if ('data' in response) {
			enqueueSnackbar(
				type == FormType.create
					? t('snackbar.createMeditationSuccess')
					: t('snackbar.editMeditationSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			if (type == FormType.create) {
				navigate(`/exercise-manage/major-list`);
			} else {
				setType(FormType.view);
			}
		}
	};

	const handleClose = () => {
		if (type == FormType.edit) return setType(FormType.view);
		navigate(`/exercise-manage/major-list`);
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const handleDeleteFile = (type: string) => {
		if (type == 'cover') {
			setCoverFile(null);
			methods.setValue('cover', '');
			imageInputRef.current && (imageInputRef.current.value = '');
		}
	};

	const handleThumbnailChange = async (e: any) => {
		e.preventDefault();
		let files;
		if (e.dataTransfer) {
			files = e.dataTransfer.files;
		} else if (e.target) {
			files = e.target.files;
		}
		const name = files[0].name;
		const reader = new FileReader();
		reader.onload = () => {
			setShowCropper(true);
			setThumbnailBase({
				base64: reader?.result,
				name: name,
			});

			imageInputRef.current && (imageInputRef.current.value = '');
		};
		reader.readAsDataURL(files[0]);
	};
	const handleCropper = async (file?: any) => {
		setShowCropper(false);
		if (!file) return;
		console.log(file);
		const res = await uploadFiles(file);
		if (res) {
			methods.setValue('cover', 'file');
			methods.trigger('cover');
			setCoverFile(res[0]);
			enqueueSnackbar(t('snackbar.uploadSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Tabs value={currentTab} onChange={handleTabChange}>
							<Tab
								label={<Typography variant='body1'>{t('day21.zh')}</Typography>}
								value='zh'
							/>
							<Tab
								label={<Typography variant='body1'>{t('day21.en')}</Typography>}
								value='en'
							/>
						</Tabs>

						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							{currentTab == 'zh' && (
								<>
									<Grid item xs={6}>
										<AppTextField
											control={methods.control}
											name='titleZh'
											label={t('day21.title')}
										/>
									</Grid>
									<Grid item xs={6}>
										<AppTextField
											control={methods.control}
											name='authorZh'
											label={t('marjorExercise.author')}
										/>
									</Grid>
									<Grid item xs={12}>
										<AppTextField
											control={methods.control}
											name='authorDescZh'
											label={t('marjorExercise.authorDesc')}
											multiline
											maxLength={500}
											minRows={4}
										/>
									</Grid>
								</>
							)}
							{currentTab == 'en' && (
								<>
									<Grid item xs={6}>
										<AppTextField
											control={methods.control}
											name='titleEn'
											label={t('day21.title_')}
											maxLength={100}
										/>
									</Grid>
									<Grid item xs={6}>
										<AppTextField
											control={methods.control}
											name='authorEn'
											label={t('marjorExercise.author_')}
										/>
									</Grid>
									<Grid item xs={12}>
										<AppTextField
											control={methods.control}
											name='authorDescEn'
											label={t('marjorExercise.authorDesc_')}
											multiline
											maxLength={500}
											minRows={4}
										/>
									</Grid>
								</>
							)}

							<Grid item xs={6}>
								<AppDropDownMenu
									control={methods.control}
									name='color'
									label={t('day21.color')}
									options={colorList}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppTextField
									control={methods.control}
									name='password'
									label={t('marjorExercise.password')}
								/>
							</Grid>
							<Grid item xs={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('day21.coverImage')}
								</Typography>
								{coverFile && (
									<img
										src={coverFile.url || ''}
										width={110}
										height={110}
										style={{
											objectFit: 'contain',
											marginRight: '8px',
										}}
									/>
								)}

								<Box display={'flex'} alignItems={'center'}>
									{coverFile ? (
										<Button
											component='label'
											sx={{ height: 24 }}
											endIcon={<LoopOutlinedIcon />}
										>
											{t('information.changeImage')}
											<VisuallyHiddenInput
												type='file'
												name='thumbnail'
												accept='image/*'
												ref={imageInputRef}
												onChange={handleThumbnailChange}
											/>
											<ImageCropper
												image={thumbnailBase}
												isOpen={showCropper}
												handleCropper={handleCropper}
												option={{
													zoomTo: 0.5,
													initialAspectRatio: 1,
													viewMode: 1,
													autoCropArea: 1,
													checkOrientation: false,
													guides: true,
													aspectRatio: 1 / 1,
												}}
											/>
										</Button>
									) : (
										<Button
											component='label'
											sx={{ height: 24 }}
											endIcon={<DriveFolderUploadOutlinedIcon />}
										>
											{t('information.uploadImage')}
											<VisuallyHiddenInput
												type='file'
												name='thumbnail'
												accept='image/*'
												ref={imageInputRef}
												onChange={handleThumbnailChange}
											/>
											<ImageCropper
												image={thumbnailBase}
												isOpen={showCropper}
												handleCropper={handleCropper}
												option={{
													zoomTo: 0.5,
													initialAspectRatio: 1,
													viewMode: 1,
													autoCropArea: 1,
													checkOrientation: false,
													guides: true,
													aspectRatio: 1 / 1,
												}}
											/>
										</Button>
									)}
									{coverFile && (
										<Button
											component='label'
											sx={{ height: 24, marginLeft: '24px' }}
											color='error'
											endIcon={<DeleteOutlinedIcon />}
											onClick={() => {
												handleDeleteFile('cover');
											}}
										>
											{t('information.deleteImage')}
										</Button>
									)}
								</Box>
								<FormHelperText color='error' style={{ color: '#d32f2f' }}>
									{coverField.fieldState.error?.message}
								</FormHelperText>
							</Grid>
						</Grid>
						<Divider />
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={12}>
								<SingleCheckbox
									control={methods.control}
									name='hasContent'
									label={t('marjorExercise.notesPage')}
								/>
							</Grid>
							{watchHasContent && currentTab == 'zh' && (
								<Grid item xs={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('marjorExercise.notesPageText')}
									</Typography>
									<AppRichTextEditor
										control={methods.control}
										name='contentZh'
									/>
								</Grid>
							)}
							{watchHasContent && currentTab == 'en' && (
								<Grid item xs={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('marjorExercise.notesPageText')}
									</Typography>
									<AppRichTextEditor
										control={methods.control}
										name='contentEn'
									/>
								</Grid>
							)}
						</Grid>
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							disabled={!needConfirm && type === FormType.create}
						>
							{type === FormType.edit ? t('button.save') : t('button.create')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
